import React from 'react';
import {
  Box, TextField, ToggleButtonGroup, ToggleButton,
  IconButton, Tooltip, Paper, InputAdornment, useTheme, useMediaQuery,
} from '@mui/material';
import { Search, FilterList, Highlight, RestartAlt } from '@mui/icons-material';

export type SearchMode = 'filter' | 'highlight';

interface LogSearchBarProps {
  searchText: string;
  onSearchTextChange: (value: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  onReset: () => void;
}

export const LogSearchBar: React.FC<LogSearchBarProps> = React.memo(({
  searchText,
  onSearchTextChange,
  searchMode,
  onSearchModeChange,
  onReset,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1, mb: 2,
        display: 'flex', alignItems: 'center', gap: 1,
        flexWrap: 'nowrap', flexShrink: 0,
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
        backgroundColor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder={isMobile ? 'Search...' : 'Search logs...'}
        value={searchText}
        onChange={(e) => onSearchTextChange(e.target.value)}
        sx={{ flex: 1, minWidth: 0 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
            </InputAdornment>
          ),
          sx: { fontSize: '0.82rem' },
        }}
      />

      <ToggleButtonGroup
        value={searchMode}
        exclusive
        onChange={(_, val) => { if (val) onSearchModeChange(val); }}
        size="small"
      >
        <ToggleButton value="filter"
          sx={{
            textTransform: 'none', fontWeight: 600,
            px: isMobile ? 1.2 : 1.5,
            py: isMobile ? 1 : 0.5,
            fontSize: '0.75rem',
            minWidth: isMobile ? 40 : 'auto',
          }}
        >
          <Tooltip title="Filter">
            <FilterList sx={{ fontSize: isMobile ? 22 : 16, mr: isMobile ? 0 : 0.5 }} />
          </Tooltip>
          {!isMobile && 'Filter'}
        </ToggleButton>
        <ToggleButton value="highlight"
          sx={{
            textTransform: 'none', fontWeight: 600,
            px: isMobile ? 1.2 : 1.5,
            py: isMobile ? 1 : 0.5,
            fontSize: '0.75rem',
            minWidth: isMobile ? 40 : 'auto',
          }}
        >
          <Tooltip title="Highlight">
            <Highlight sx={{ fontSize: isMobile ? 22 : 16, mr: isMobile ? 0 : 0.5 }} />
          </Tooltip>
          {!isMobile && 'Highlight'}
        </ToggleButton>
      </ToggleButtonGroup>

      <Tooltip title="Reset search">
        <IconButton
          size={isMobile ? 'medium' : 'small'}
          onClick={onReset}
          sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            p: isMobile ? 1 : 0.5,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <RestartAlt sx={{ fontSize: isMobile ? 22 : 20 }} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
});

LogSearchBar.displayName = 'LogSearchBar';
